import type { LoginData, PreLoginData, UserCompany } from '@/features/auth/types/auth.types';

type BffErrorPayload = {
  code?: string;
  message?: string;
};

/**
 * Unwraps the standard BFF envelope `{ response, data, error?, meta? }`.
 * Throws when `response === 'BAD'`.
 */
export function unwrapBffEnvelope<T>(raw: unknown): T {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid BFF response');
  }

  const root = raw as Record<string, unknown>;

  if (root.response === 'BAD') {
    const err = root.error as BffErrorPayload | undefined;
    throw new Error(err?.message ?? err?.code ?? 'BFF request failed');
  }

  if (root.data === undefined) {
    throw new Error('Missing data in BFF response');
  }

  return root.data as T;
}

/**
 * Login / refresh: tokens live at `response.data.*` (single `data` level after BFF normalization).
 */
export function unwrapAuthTokens(raw: unknown): LoginData {
  const data = unwrapBffEnvelope<Record<string, unknown>>(raw);

  const tokens =
    typeof data.access_token === 'string'
      ? data
      : ((data.data as Record<string, unknown> | undefined) ?? data);

  const accessToken = tokens.access_token;
  const refreshToken = tokens.refresh_token;

  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
    throw new Error('Missing access_token or refresh_token in auth response');
  }

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_in: Number(tokens.expires_in ?? 900),
    token_type: String(tokens.token_type ?? 'Bearer'),
    user_uuid:
      typeof tokens.user_uuid === 'string'
        ? tokens.user_uuid
        : typeof (tokens.user as Record<string, unknown> | undefined)?.user_id === 'string'
          ? String((tokens.user as Record<string, unknown>).user_id)
          : undefined,
    companies: Array.isArray(tokens.companies) ? (tokens.companies as UserCompany[]) : undefined,
    active_company:
      tokens.active_company && typeof tokens.active_company === 'object'
        ? (tokens.active_company as UserCompany)
        : undefined,
  };
}

/**
 * Pre-login may be flat or nested depending on upstream shape.
 */
export function unwrapPreLoginData(raw: unknown): PreLoginData {
  const data = unwrapBffEnvelope<Record<string, unknown>>(raw);

  const inner =
    data.temp_token != null
      ? data
      : ((data.data as Record<string, unknown> | undefined) ?? data);

  const tempToken = inner.temp_token;
  if (typeof tempToken !== 'string') {
    throw new Error('Missing temp_token in pre-login response');
  }

  return {
    mfa_enabled: Boolean(inner.mfa_enabled),
    temp_token: tempToken,
    expires_at: String(inner.expires_at ?? ''),
    user_uuid: typeof inner.user_uuid === 'string' ? inner.user_uuid : undefined,
    companies: Array.isArray(inner.companies) ? (inner.companies as UserCompany[]) : undefined,
  };
}
